import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  IconMailCheck,
  IconShieldCheck,
  IconClock,
  IconLock,
  IconLoader2,
  IconAlertCircle,
  IconCircleCheck,
} from "@tabler/icons-react";
import classes from "./verifyRedesign.module.css";
import { talentResendOtp, talentVerifyOtp } from "../../../services/talentCalls";
import { clientResendOtp, clientVerifyOtp } from "../../../services/clientCalls";
import { errorHandler, toastSuccess } from "../../../utils";
import { Logo } from "../../../constants";

const BrandLogo = () => (
  <a className={classes.brandLogo} href="https://www.awasource.com">
    <Logo color="#ffffff" />
  </a>
);

const RESEND_SECONDS = 90;

const VerifyRedesign = () => {
  const navigate = useNavigate();
  const [param] = useSearchParams();
  const status = param.get("status") === "client" ? "client" : "talent";
  const email = localStorage.getItem("email");

  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const [otpErr, setOtpErr] = useState(false);
  const [shake, setShake] = useState(false);
  const [load, setLoad] = useState(false);
  const [success, setSuccess] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(RESEND_SECONDS);
  const inputRefs = useRef([]);

  /* Lock page scroll on desktop, where this is a fixed one-screen split layout */
  useEffect(() => {
    const mql = window.matchMedia("(min-width: 981px)");
    const applyLock = () => {
      const locked = mql.matches ? "hidden" : "";
      document.documentElement.style.overflow = locked;
      document.body.style.overflow = locked;
    };
    applyLock();
    mql.addEventListener("change", applyLock);
    return () => {
      mql.removeEventListener("change", applyLock);
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
    };
  }, []);

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const id = setInterval(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearInterval(id);
  }, [secondsLeft]);

  const formatTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  const code = digits.join("");

  const handleDigitChange = (idx, value) => {
    const v = value.replace(/[^0-9]/g, "").slice(0, 1);
    setDigits((prev) => {
      const next = [...prev];
      next[idx] = v;
      return next;
    });
    setOtpErr(false);
    if (v && idx < 5) inputRefs.current[idx + 1]?.focus();
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !digits[idx] && idx > 0) {
      inputRefs.current[idx - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData("text").replace(/[^0-9]/g, "");
    if (!pasted) return;
    e.preventDefault();
    const next = pasted.slice(0, 6).split("");
    setDigits((prev) => prev.map((d, i) => next[i] ?? d));
    const focusIdx = Math.min(next.length, 5);
    inputRefs.current[focusIdx]?.focus();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (code.length < 6) {
      setOtpErr(true);
      setShake(true);
      setTimeout(() => setShake(false), 400);
      return;
    }

    try {
      setLoad(true);
      const res = status === "talent" ? await talentVerifyOtp(code) : await clientVerifyOtp(code);
      localStorage.setItem("token", res?.data?.data?.token);
      setSuccess(true);
      setTimeout(() => navigate("/welcome", { state: status }), 900);
    } catch (error) {
      setLoad(false);
      error?.response?.data
        ? errorHandler(error?.response?.data?.errors, "Error")
        : errorHandler("Network error: Something went wrong, Please check your internet connection and try again.", "Error");
    }
  };

  const resendOtp = async () => {
    try {
      setSecondsLeft(RESEND_SECONDS);
      if (status === "talent") await talentResendOtp();
      else await clientResendOtp();
      toastSuccess(`Check ${email || "your inbox"} for the new code`);
    } catch (error) {
      error?.response?.data
        ? errorHandler(error?.response?.data?.errors, "Error")
        : errorHandler("Network error: Something went wrong, Please check your internet connection and try again.", "Error");
    }
  };

  return (
    <div className={classes.authShell}>
      <div className={classes.pageBg}>
        <div className={`${classes.brandBlob} ${classes.b1}`} />
        <div className={`${classes.brandBlob} ${classes.b2}`} />
        <div className={`${classes.brandBlob} ${classes.b3}`} />
      </div>

      <aside className={classes.brandPanel}>
        <div className={classes.brandTop}>
          <BrandLogo />
          <span className={classes.brandPanelTag}>Africa's hiring layer</span>
        </div>

        <div className={classes.brandMiddle}>
          <h1 className={classes.brandHeadline}>
            Just one
            <br />
            more step.
          </h1>
          <p className={classes.brandSub}>
            We've sent a verification code to your inbox. Confirm it's really you and your account will be ready to go.
          </p>

          <div className={classes.brandVisual}>
            <div className={`${classes.floatCard} ${classes.fcA}`}>
              <span className={classes.fcIcon} style={{ background: "#ecfdf5", color: "#059669" }}>
                <IconShieldCheck size={16} />
              </span>
              <div>
                <div className={classes.fcTitle}>Secure by design</div>
                <div className={classes.fcSub}>Bank-level encryption</div>
              </div>
            </div>
            <div className={`${classes.floatCard} ${classes.fcB}`}>
              <span className={classes.fcIcon} style={{ background: "#eff2ff", color: "#1d4ed8" }}>
                <IconClock size={16} />
              </span>
              <div>
                <div className={classes.fcTitle}>Code expires in 10 min</div>
                <div className={classes.fcSub}>For your security</div>
              </div>
            </div>
            <div className={`${classes.floatCard} ${classes.fcC}`}>
              <span className={classes.fcIcon} style={{ background: "#fef3c7", color: "#d97706" }}>
                <IconLock size={16} />
              </span>
              <div>
                <div className={classes.fcTitle}>One-time use only</div>
                <div className={classes.fcSub}>Never reused or shared</div>
              </div>
            </div>
          </div>
        </div>

        <div className={classes.brandBottom}>
          <div className={classes.brandBottomAvatars}>
            <span>AO</span>
            <span>KM</span>
            <span>NB</span>
          </div>
          <p className={classes.brandBottomText}>
            <strong>10k+</strong> verified profiles
          </p>
        </div>
      </aside>

      <div className={classes.mobileHeaderBar}>
        <div className={classes.mobileHeaderBarInner}>
          <BrandLogo />
        </div>
      </div>

      <main className={classes.formPanel}>
        <div className={classes.formCard}>
          <div className={classes.formPanelInner}>
            <div className={`${classes.formHeader} ${classes.animIn}`}>
              <span className={classes.formEyebrow}>
                <IconMailCheck size={13} /> Verify account
              </span>
              <h1 className={classes.formTitle}>Verify Email</h1>
              <p className={classes.formSubtitle}>
                Please enter the code sent to <strong>{email || "your email address"}</strong> below. Alternatively, you can
                click the link sent to your email address.
              </p>
            </div>

            <form className={classes.animIn} onSubmit={submit} noValidate>
              <div className={classes.field}>
                <label className={classes.fieldLabel}>Enter code here</label>
                <div className={`${classes.otpRow} ${shake ? classes.shake : ""}`}>
                  {digits.map((d, idx) => (
                    <input
                      key={idx}
                      ref={(el) => (inputRefs.current[idx] = el)}
                      className={`${classes.otpBox} ${otpErr ? classes.otpBoxErr : ""}`}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={d}
                      onChange={(e) => handleDigitChange(idx, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(idx, e)}
                      onPaste={handlePaste}
                    />
                  ))}
                </div>
                <div className={`${classes.fieldError} ${otpErr ? classes.fieldErrorOn : ""}`}>
                  <IconAlertCircle size={13} /> Enter the full 6-digit code.
                </div>
              </div>

              <button type="submit" className={classes.btnPrimary} disabled={load || success}>
                {load ? (
                  <>
                    <IconLoader2 size={16} className={classes.spinIcon} /> Verifying…
                  </>
                ) : (
                  "Verify Email"
                )}
              </button>

              {success && (
                <div className={`${classes.successBanner} ${classes.on}`}>
                  <IconCircleCheck size={18} />
                  <span>Email verified — redirecting…</span>
                </div>
              )}
            </form>

            <p className={classes.resendRow}>
              Didn't receive a code?{" "}
              {secondsLeft > 0 ? (
                <span className={classes.resendTimer}>Resend link in {formatTime(secondsLeft)}</span>
              ) : (
                <button type="button" className={classes.resendLink} onClick={resendOtp}>
                  Resend code
                </button>
              )}
            </p>
            <p className={classes.changeEmailRow}>
              Or <Link to="/register">click here</Link> to change the email address.
            </p>

            <p className={classes.formFooter}>
              By proceeding you have agreed to our <Link to="/terms-of-service">Terms of Use</Link> &amp;{" "}
              <Link to="/privacy-policy">Privacy Policy</Link>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default VerifyRedesign;
